import { yupResolver } from "@hookform/resolvers/yup";
import cn from "@utils/cn";
import { ComponentFilters, FiltrableArticleAttributes } from "@utils/damac/api";
import { useLocale, useTranslations } from "next-intl";
import React, { FC, useState } from "react";
import { useForm } from "react-hook-form";
import { InferType, object, string } from "yup";

import Button, { ButtonSize, ButtonTheme, ButtonVariant } from "@/components/ui/Button/Button";
import useMediaQuery from "@/hooks/useMediaQuery";
import { ArticleTypes } from "@/types";

import { ContactInformationSchemaType } from "../Checkout/ContactInformation";
import RadioFilterList from "../SearchPanelFilters/RadioFilterList";
import { ContactInformationSchema } from "../SoftLogin/LoginStart";
import Select from "../ui/Form/Select/Select";
import { SelectOptions } from "../ui/Form/Select/SelectBase";
import Icon from "../ui/Icon/Icon";
import Modal from "../ui/Modal/Modal";
import ModalContent from "../ui/Modal/ModalContent";
import filter from "./filter";

interface Props {
  articleType: ArticleTypes;
  filters: FiltrableArticleAttributes;
  filterFunction: Function;
  setArticles: Function;
  setArticlesFilters: Function;
  featuredSorting: boolean;
}

export const ArticleFiltersSchema = object()
  .shape({
    topic: string(),
    year: string(),
    month: string(),
    media: string(),
  })
  .required();

export type ArticleFiltersSchemaType = InferType<typeof ArticleFiltersSchema>;

const ArticleFilters: FC<Props> = ({ articleType, filters, filterFunction, setArticles, setArticlesFilters, featuredSorting }) => {
  const [modalOpen, setModalOpen] = useState<boolean>(false);
  const isDesktop = useMediaQuery("(min-width: 1024px)");

  const t = useTranslations();
  const locale = useLocale();
  const [language, regions] = locale.split("-");

  const { control, setValue, watch } = useForm<ArticleFiltersSchemaType>({
    resolver: yupResolver(ArticleFiltersSchema),
    defaultValues: {},
    mode: "onChange",
  });

  let topicFilter: SelectOptions[] =
    filters.tags?.map((tag) => {
      return {
        label: t("article.tag." + tag),
        value: tag,
      };
    }) || [];
  topicFilter.unshift({ label: t("topics.all"), value: "" });

  let yearFilter: SelectOptions[] =
    filters.years?.map((year) => {
      return {
        label: year,
        value: year,
      };
    }) || [];
  yearFilter.unshift({ label: t("years.all"), value: "" });

  let months = [];
  for (let index = 0; index < 12; index++) {
    let date = new Date();
    date.setMonth(index);
    months.push(
      date.toLocaleString(locale, {
        month: "long",
      }),
    );
  }

  let date = new Date();
  let monthFilter: SelectOptions[] =
    filters.months?.map((month) => {
      date.setMonth(Number.parseInt(month));
      return {
        label: date.toLocaleString(locale, {
          month: "long",
        }),
        value: month,
      };
    }) || [];
  monthFilter.unshift({ label: t("months.all"), value: "" });

  let mediaFilter: SelectOptions[] = [
    {
      label: t("article"),
      value: "false",
    },
    {
      label: t("video"),
      value: "true",
    },
  ];

  mediaFilter.unshift({ label: t("all"), value: "" });

  const clearFilters = () => {
    let radioFilters = [];
    radioFilters.push(document.querySelectorAll("input[name=topic]"));
    radioFilters.push(document.querySelectorAll("input[name=year]"));
    radioFilters.push(document.querySelectorAll("input[name=month]"));
    radioFilters.push(document.querySelectorAll("input[name=media]"));

    radioFilters.forEach((buttons) => {
      buttons.forEach((button: any) => {
        button.checked = false;
      });
    });
  };

  const showResults = () => {
    let articleFilters: ComponentFilters = {
      articleType: [
        {
          value: articleType,
        },
      ],
    };
    let selectedTopic = document.querySelector("input[name=topic]:checked") as HTMLInputElement;
    let topicSelect = document.querySelector("select[name=topic]") as HTMLSelectElement;
    if (!selectedTopic || selectedTopic.value == "") {
      delete articleFilters.tags;
      topicSelect.options.selectedIndex = 0;
    } else {
      articleFilters.tags = [
        {
          value: selectedTopic.value,
          operator: "[in]",
        },
      ];
      for (let index = 0; index < topicSelect.options.length; index++) {
        if (topicSelect.options[index].value === selectedTopic.value) {
          topicSelect.options.selectedIndex = index;
          break;
        }
      }
    }

    let selectedYear = document.querySelector("input[name=year]:checked") as HTMLInputElement;
    if (!selectedYear || selectedYear.value == "") {
      delete articleFilters.postDate;
    } else {
      articleFilters.postDate = [
        {
          value: new Date(selectedYear.value.toString()).toISOString(),
          operator: "[gte]",
        },
        {
          value: new Date((parseInt(selectedYear.value) + 1).toString()).toISOString(),
          operator: "[lt]",
        },
      ];
    }

    let selectedMonth = document.querySelector("input[name=month]:checked") as HTMLInputElement;
    if (!selectedMonth || selectedMonth.value == "") {
      if (!selectedYear) {
        delete articleFilters.postDate;
      }
    } else {
      let date = new Date(new Date().getFullYear().toString());
      date.setMonth(parseInt(selectedMonth.value));
      if (!selectedYear || selectedYear.value == "") {
        let yearToSelect = document.querySelector(
          'input[name=year][value="' + (date > new Date() ? date.getFullYear().toString() : new Date().getFullYear().toString()) + '"]',
        ) as HTMLInputElement;
        if (yearToSelect) {
          yearToSelect.checked = true;
        }
      }
      selectedYear = document.querySelector("input[name=year]:checked") as HTMLInputElement;
      date.setFullYear(parseInt(selectedYear.value));
      articleFilters.postDate = [
        {
          value: date.toISOString(),
          operator: "[gte]",
        },
      ];
      date.setMonth(date.getMonth() + 1);
      articleFilters.postDate.push({
        value: date.toISOString(),
        operator: "[lt]",
      });
    }

    let selectedMedia = document.querySelector("input[name=media]:checked") as HTMLInputElement;
    if (!selectedMedia || selectedMedia.value == "") {
      delete articleFilters.video;
    } else {
      articleFilters.video = [
        {
          value: selectedMedia.value,
          operator: "[exists]",
        },
      ];
    }

    setArticlesFilters(articleFilters);

    filter(articleFilters, !isDesktop, featuredSorting).then((res) => {
      setArticles(res);
    });

    let filterButton = document.querySelector("button[id=articlesFilters]");
    if (filterButton) {
      if (articleFilters.tags || articleFilters.postDate || articleFilters.video) {
        filterButton.classList.remove("btn-primary-white");
        filterButton.classList.add("btn-primary-black");
      } else {
        filterButton.classList.remove("btn-primary-black");
        filterButton.classList.add("btn-primary-white");
      }
    }
  };

  return (
    <div className="w-full pt-xxl px-l">
      <div className="md:w-20\/24 md:mx-auto md:border-b md:border-t border-gray-10 flex md:grid md:grid-cols-4 md:gap-l md:py-s items-center">
        <div key="0" className={cn("article-filter border border-gray-10 md:border-none rounded-full w-full mr-m px-m md:px-0")}>
          <Select
            name={"topic"}
            label={t("topic")}
            control={control}
            options={topicFilter}
            className="py-xxs"
            onChange={(e) => filterFunction(e)}
          ></Select>
        </div>
        <div key="1" className={cn("article-filter border border-gray-10 md:border-none rounded-full w-full mr-m px-m md:px-0", "hidden md:block")}>
          <Select
            name={"year"}
            control={control}
            label={t("year")}
            options={yearFilter}
            className={`py-xxs ${language === "ar" ? "none" : "p-0"}`}
            onChange={(e) => filterFunction(e)}
          ></Select>
        </div>
        <div key="2" className={cn("article-filter border border-gray-10 md:border-none rounded-full w-full mr-m px-m md:px-0", "hidden md:block")}>
          <Select
            name={"month"}
            control={control}
            label={t("month")}
            options={monthFilter}
            className="py-xxs"
            onChange={(e) => filterFunction(e)}
          ></Select>
        </div>
        {/* <div key="3" className={cn("article-filter border border-gray-10 md:border-none rounded-full w-full mr-m px-m md:px-0", "hidden md:block")}>
          <Select
            name={"media"}
            control={control}
            label={t("media")}
            options={mediaFilter}
            className="py-xxs"
            onChange={(e) => filterFunction(e)}
          ></Select>
        </div> */}

        <Button
          size={ButtonSize.SMALL}
          variant={ButtonVariant.PRIMARY}
          theme={ButtonTheme.WHITE}
          onClick={() => setModalOpen(true)}
          customClass="p-s rounded-full !h-[50px] md:!hidden"
          id="articlesFilters"
        >
          <Icon name="filters" size="16" title="Play Video" />
          <div className="sr-only">{t("see.video")}</div>
        </Button>

        <Modal open={modalOpen} onClose={() => setModalOpen(false)} className="md:hidden ">
          <ModalContent className="w-full">
            <div className="h-dvh relative">
              <div className="flex justify-between">
                <p className="font-primary font-medium text-m uppercase py-l sticky top-0 bg-white">{t("label.filters.title")}</p>
                <Button
                  className="modal__close"
                  size={ButtonSize.MEDIUM}
                  variant={ButtonVariant.TERTIARY}
                  theme={ButtonTheme.BLACK}
                  onClick={() => setModalOpen(false)}
                >
                  <span className="pointer-events-none">
                    <Icon name="close" size="24" title="close" className="stroke-black" />
                    <span className="sr-only">{t("button.label.closeModal")}</span>
                  </span>
                </Button>
              </div>
              <RadioFilterList key={0} filter={{ valueList: topicFilter as any, title: "topic" }} name={"topic"} />
              <RadioFilterList key={1} filter={{ valueList: yearFilter as any, title: "year" }} name={"year"} />
              <RadioFilterList key={2} filter={{ valueList: monthFilter as any, title: "month" }} name={"month"} />
              <RadioFilterList key={3} filter={{ valueList: mediaFilter as any, title: "media" }} name={"media"} />
              <div className="flex items-center justify-between py-m my-l sticky top-[100%]">
                <Button
                  size={ButtonSize.SMALL}
                  variant={ButtonVariant.TERTIARY}
                  theme={ButtonTheme.BLACK}
                  customClass="font-secondary font-medium text-m  normal-case tracking-normal underline-offset-2 p-0 "
                  onClick={() => clearFilters()}
                >
                  {t("label.filters.clear")}
                </Button>

                <Button
                  size={ButtonSize.MEDIUM}
                  variant={ButtonVariant.PRIMARY}
                  theme={ButtonTheme.BLACK}
                  customClass=" text-s font-medium font-primary"
                  onClick={() => {
                    showResults();
                    setModalOpen(false);
                  }}
                >
                  <>{t("results.show")}</>
                </Button>
              </div>
            </div>
          </ModalContent>
        </Modal>
      </div>
    </div>
  );
};

export default ArticleFilters;
